import './FinancialAssistance.scss';

import dayjs from 'dayjs';
import { Formik } from 'formik';
import { Container, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { IFinancialAssistanceForm, InsuranceStatus } from '@customTypes/requisition';
import ContentContainer from '@layouts/container/ContentContainer';
import PageContainer from '@layouts/container/PageContainer';
import { submitFinancialAssistance } from '@store/actions';

import FinancialAssistanceForm from './components/form/FinancialAssistanceForm';
import InformationBox from './components/InformationBox/InformationBox';

const MAX_DATE = dayjs().endOf('day').toDate();
const MIN_DATE = dayjs().subtract(100, 'year').startOf('day').toDate();

const validationSchema = yup
  .object()
  .shape({
    household_size: yup.number().required('Required'),
    household_income: yup.number().required('Required'),
    estimated_max_cost: yup.number().required('Required'),
    first_name: yup.string().required('Required'),
    last_name: yup.string().required('Required'),
    date_of_birth: yup
      .date()
      .required('Required')
      .transform(v => (v ? dayjs(v).toDate() : v))
      .min(MIN_DATE, 'It can not be over 100 years old')
      .max(MAX_DATE, 'It can not be in the future'),
    requisition_id: yup.string(),
    insurance_status: yup.string().oneOf([InsuranceStatus.INSURED, InsuranceStatus.NOT_INSURED]).required('Required'),
    email: yup.string().email('Invalid email'),
    phone_number: yup.string(),
    tos_consent: yup.boolean().oneOf([true], 'You must agree to the terms of service').required('Required'),
    signature: yup.string().required('Required'),
    datetime_signed: yup
      .date()
      .required('Required')
      .transform(v => (v ? dayjs(v).toDate() : v))
      .max(MAX_DATE, 'It can not be in the future'),
  })
  .test('email-or-phone', 'Email or phone required', function (value) {
    if (!value.email && !value.phone_number) {
      return this.createError({
        path: 'email-or-phone', // this is the key that will appear in Formik's `errors`
        message: 'Email or phone required',
      });
    }
    return true;
  });

const FinancialAssistance = () => {
  const navigate = useNavigate();

  const onSubmit = async (data: IFinancialAssistanceForm) => {
    try {
      const { message } = await submitFinancialAssistance(data);
      navigate('/access/success', {
        state: { message },
      });
    } catch (err) {
      console.error('Error submitting financial assistance:', err);
    }
  };

  return (
    <PageContainer>
      <ContentContainer containerClass='bg-body py-4' contentClass='financial-assistance-header'>
        <h1 className='text-center text-primary fw-bold'>Financial Assistance Application</h1>
        <InformationBox />
      </ContentContainer>

      <Container fluid='xxl'>
        <Stack direction='vertical' gap={4} className='bg-white rounded-3 financial-assistance-content'>
          <Formik<IFinancialAssistanceForm>
            validateOnChange
            validationSchema={validationSchema}
            initialValues={
              {
                datetime_signed: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              } as IFinancialAssistanceForm
            }
            onSubmit={onSubmit}
          >
            <FinancialAssistanceForm />
          </Formik>
        </Stack>
      </Container>
    </PageContainer>
  );
};
export default FinancialAssistance;
